import { FC, useMemo } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { makeStyles } from "tss-react/mui";
import {
  MiAlert,
  MiButton,
  MiCircleMarker,
  MiIcon,
  MiInputTextbox,
  MiMap,
} from "@miplatform/components";
import { useMaps } from "@miplatform/hooks";
import { ButtonVariants, Colors } from "@miplatform/types";
import { createUUID, getLocationBoundaries } from "@miplatform/utils";
import { mdiMinusCircle, mdiPlus } from "@mdi/js";
import { Box } from "@mui/material";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  MIN_SERVICE_AREA_RADIUS,
  SERVICE_AREAS_LIMIT,
} from "../../ConfigCalendarSetList.constants";
import { ICalendarSetData } from "../../ConfigCalendarSetList.types";
import { ServiceAreaAutocomplete } from "./ServiceAreaAutocomplete";

export const CalendarServiceAreasTabView: FC<
  CalendarServiceAreasTabViewProps
> = ({
  form,
  extraTabProps: { isReadOnly } = {},
}: CalendarServiceAreasTabViewProps) => {
  const { classes, theme } = useStyles();
  const {
    control,
    setValue,
    trigger,
    formState: { errors },
  } = form;
  const { getLatLng } = useMaps();

  const serviceAreas = useWatch({
    control,
    name: "serviceAreas",
  });
  const validServiceAreas = useMemo(
    () =>
      serviceAreas.filter(
        (area) =>
          area.city &&
          area.state &&
          area.radius &&
          parseFloat(area.radius) >= MIN_SERVICE_AREA_RADIUS
      ),
    [serviceAreas]
  );
  const uniqueServiceAreas = useMemo(
    () => new Set(serviceAreas.map(({ city, state }) => `${city}-${state}`)),
    [serviceAreas]
  );

  const { position, zoomFactor } = useMemo(
    () =>
      getLocationBoundaries(
        validServiceAreas.map((area) => ({
          ...area.location,
          Radius: Number.isNaN(parseFloat(area.radius))
            ? 0
            : parseFloat(area.radius),
        }))
      ),
    [validServiceAreas]
  );

  const handleAddServiceArea = () => {
    const newServiceAreas = [...serviceAreas];
    newServiceAreas.push({
      id: createUUID(),
      city: "",
      state: "",
      radius: "20",
      location: { Latitude: 0, Longitude: 0 },
    });
    setValue("serviceAreas", newServiceAreas, { shouldDirty: true });
  };

  const handleRemoveServiceArea = (index: number) => {
    const newServiceAreas = [...serviceAreas];
    newServiceAreas.splice(index, 1);
    setValue("serviceAreas", newServiceAreas, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleGetLatLng = async (
    city: string,
    state: string,
    index: number
  ) => {
    const location = await getLatLng({ state, city });
    if (location) {
      setValue(`serviceAreas.${index}.location`, {
        Latitude: location.lat,
        Longitude: location.lng,
      });
      trigger("serviceAreas");
    } else {
      setValue(`serviceAreas.${index}.city`, "");
      setValue(`serviceAreas.${index}.state`, "");
    }
  };

  const renderServiceAreas = () =>
    serviceAreas.map((area, index) => {
      return (
        <Box key={area.id} className={classes.serviceAreaContainer}>
          <Box width="50%">
            <ServiceAreaAutocomplete
              value={
                area.city && area.state ? `${area.city}, ${area.state}` : ""
              }
              city={area.city}
              state={area.state}
              isReadOnly={isReadOnly}
              error={errors?.serviceAreas?.[index]?.city}
              onBlur={() => {
                if (!area.city) {
                  setValue(`serviceAreas.${index}.city`, "", {
                    shouldValidate: true,
                  });
                }
              }}
              onChange={(value: { city: string; state: string }) => {
                setValue(`serviceAreas.${index}.city`, value.city, {
                  shouldDirty: true,
                });
                setValue(`serviceAreas.${index}.state`, value.state, {
                  shouldDirty: true,
                });
                handleGetLatLng(value.city, value.state, index);
              }}
            />
          </Box>
          <NumericFormat
            required
            thousandSeparator
            customInput={MiInputTextbox}
            decimalScale={0}
            allowNegative={false}
            suffix=" Miles"
            label="Radius"
            width="50%"
            value={area.radius}
            error={!!errors?.serviceAreas?.[index]?.radius}
            helperText={errors?.serviceAreas?.[index]?.radius?.message}
            shrink
            sx={{ flex: 1 }}
            disabled={isReadOnly}
            onValueChange={(values) => {
              const { floatValue } = values;

              setValue(
                `serviceAreas.${index || 0}.radius`,
                floatValue?.toString() || "",
                { shouldDirty: true, shouldValidate: true }
              );
            }}
          />

          {!isReadOnly && (
            <Box display="flex" alignItems="center" height={40}>
              <MiIcon
                path={mdiMinusCircle}
                color={theme.palette.error.main}
                onClick={() => handleRemoveServiceArea(index)}
                style={{ alignSelf: "center" }}
                size={0.75}
              />
            </Box>
          )}
        </Box>
      );
    });

  return (
    <>
      <ConfigAccordionHeader title="Service Areas">
        {renderServiceAreas()}
        {!isReadOnly && (
          <Box display="flex" justifyContent="flex-end">
            <MiButton
              startIcon={{ path: mdiPlus }}
              variant={ButtonVariants.Contained}
              onClick={handleAddServiceArea}
              disabled={
                serviceAreas.length !== uniqueServiceAreas.size ||
                serviceAreas.length >= SERVICE_AREAS_LIMIT ||
                serviceAreas.some(
                  (area) =>
                    !area.city ||
                    !area.radius ||
                    parseFloat(area.radius) < MIN_SERVICE_AREA_RADIUS
                )
              }
              title="Add Service Area"
            />
          </Box>
        )}
        <MiAlert
          description="Configure the service area for your calendar. This will impact the map view for your calendar"
          sx={{ mt: 2, py: 0, px: 2 }}
        />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader
        title="Service Area Preview"
        color={Colors.Secondary}
      >
        <MiMap
          zoomFactor={zoomFactor}
          mapOptions={{
            zoomControl: false,
            disableDefaultUI: true,
            clickableIcons: false,
            draggable: false,
            scrollwheel: false,
            disableDoubleClickZoom: true,
          }}
          {...(position
            ? { center: { lat: position.lat, lng: position.lng } }
            : {})}
          containerProps={{ className: classes.map }}
        >
          {validServiceAreas.map(({ location, radius }, index) => (
            <MiCircleMarker
              key={index}
              text={`${radius} Miles`}
              center={{ lat: location.Latitude, lng: location.Longitude }}
              radius={parseInt(radius, 10)}
            />
          ))}
        </MiMap>
      </ConfigAccordionHeader>
    </>
  );
};

interface CalendarServiceAreasTabViewProps {
  form: UseFormReturn<ICalendarSetData>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}

const useStyles = makeStyles()((theme) => ({
  serviceAreaContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  map: {
    width: "100%",
    height: 500,
  },
}));
